import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';

export default function WelcomeStep({ onNext, currentUser }) {
  const { userProfile } = useAuth();
  const displayName = currentUser?.displayName || userProfile?.displayName || 'there';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }} />
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-3 h-3 bg-blue-400 rounded-full opacity-20 floating-shape blur-sm" style={{ animationDelay: '0s' }} />
        <div className="absolute top-32 right-20 w-2 h-2 bg-green-400 rounded-full opacity-20 floating-shape blur-sm" style={{ animationDelay: '1s' }} />
        <div className="absolute top-40 left-1/4 w-4 h-4 bg-purple-400 rounded-full opacity-20 floating-shape blur-sm" style={{ animationDelay: '2s' }} />
        <div className="absolute top-60 right-1/3 w-2 h-2 bg-cyan-400 rounded-full opacity-20 floating-shape blur-sm" style={{ animationDelay: '0.5s' }} />
      </div>

      <div className="relative z-20 max-w-2xl mx-auto px-4 pt-4 text-center animate-fade-in-up">
        {/* Brand Icon */}
        <div className="mb-6 mt-2">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-2xl shadow-blue-500/25 relative">
            {/* Inner glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full opacity-50 blur-xl"></div>
            <span className="text-lg font-bold text-white relative z-10">CI</span>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            Welcome to<br/>Crypto Indicators
          </h1>
          <p className="text-base text-gray-300 leading-relaxed">
            Let's personalize your crypto insights experience.
          </p>
        </div>

        {/* What you'll get section */}
        <div className="mb-6">
          <h2 className="font-semibold text-white mb-3 text-lg">
            What you'll get:
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-green-500/25 group-hover:shadow-xl group-hover:shadow-green-500/40 transition-all duration-300">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-base">Real-time Analytics</h3>
              <p className="text-gray-400 text-xs leading-relaxed">
                Live cryptocurrency data with advanced technical indicators
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-blue-500/25 group-hover:shadow-xl group-hover:shadow-blue-500/40 transition-all duration-300">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-base">AI Insights</h3>
              <p className="text-gray-400 text-xs leading-relaxed">
                Intelligent analysis and trading recommendations
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-purple-500/25 group-hover:shadow-xl group-hover:shadow-purple-500/40 transition-all duration-300">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2 text-base">Personalized Experience</h3>
              <p className="text-gray-400 text-xs leading-relaxed">
                Customized dashboard based on your preferences
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mb-4">
          <button
            onClick={() => onNext()}
            className="group relative px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 text-base shadow-lg shadow-blue-500/25 transform hover:scale-105 active:scale-95 overflow-hidden"
          >
            {/* Button shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            <span className="relative z-10">Let's Get Started</span>
          </button>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-500">
          Quick setup • Takes only a few minutes
        </p>
      </div>
    </div>
  );
} 